import { useContext, useState, useEffect } from 'react';
import { Store } from '../Store';
import { Helmet } from 'react-helmet-async';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import MessageBox from '../components/MessageBox';
import ListGroup from 'react-bootstrap/ListGroup';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function CartScreen() {
  const navigate = useNavigate();
  const { state, dispatch: ctxDispatch } = useContext(Store);
  const {
    cart: { cartItems, discount = 0, promoCode = '' },
  } = state;

  const [promoCodeInput, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');

  const updateCartHandler = async (item, quantity) => {
    const { data } = await axios.get(`/api/products/${item._id}`);
    if (data.countInStock < quantity) {
      window.alert('Sorry. Product is out of stock');
      return;
    }
    ctxDispatch({
      type: 'CART_ADD_ITEM',
      payload: { ...item, quantity, size: item.size },
    });
  };

  function removeItemHandler(item) {
    ctxDispatch({ type: 'CART_REMOVE_ITEM', payload: item });
  }

  function checkOutHandler() {
    navigate('/signin?redirect=/shipping');
  }

  const applyPromoHandler = (e) => {
    e.preventDefault();
    ctxDispatch({
      type: 'CART_APPLY_PROMO',
      payload: { promoCode: promoCodeInput, discount: 0 }, // discount will be set by useEffect
    });
    setPromoError('');
  };

  useEffect(() => {
    const applyPromo = async () => {
      if (promoCode) {
        try {
          const { data } = await axios.post('/api/promos/apply', {
            code: promoCode,
            cartTotal: cartItems.reduce((a, c) => a + c.price * c.quantity, 0),
          });
          ctxDispatch({
            type: 'CART_APPLY_PROMO',
            payload: { promoCode, discount: data.discount },
          });
        } catch (err) {
          ctxDispatch({
            type: 'CART_APPLY_PROMO',
            payload: { promoCode: '', discount: 0 },
          });
        }
      } else {
        ctxDispatch({
          type: 'CART_APPLY_PROMO',
          payload: { promoCode: '', discount: 0 },
        });
      }
    };
    applyPromo();
  }, [cartItems, promoCode, ctxDispatch]);

  return (
    <div>
      <Helmet>
        <title>Shopping Cart</title>
      </Helmet>
      <h1>Shopping Cart</h1>
      <Row>
        <Col md={8}>
          {cartItems.length === 0 ? (
            <MessageBox>
              Cart is empty. <Link to="/">Go Shopping</Link>
            </MessageBox>
          ) : (
            <ListGroup>
              {cartItems.map((item) => (
                <ListGroup.Item key={`${item._id}-${item.size}-${item.color ?? ''}`}>
                  <Row className="align-items-center">
                    <Col md={3}>
                      <img
                        src={
                          item.colors?.length > 0 && item.color
                            ? item.colors.find((c) => c.name === item.color)
                                ?.images?.[0] ?? item.image[0]
                            : item.image[0]
                        }
                        alt={item.name}
                        className="img-fluid rounded img-thumbnail"
                      ></img>{' '}
                      <Link to={`/product/${item.slug}`}>{item.name}</Link>
                    </Col>
                    <Col md={2}>
                      <div>Size: {item.size}</div>
                      {item.color && <div>Color: {item.color}</div>}
                    </Col>
                    <Col md={2}>{item.price} EGP</Col>
                    <Col md={3}>
                      <Button
                        onClick={() =>
                          updateCartHandler(item, item.quantity - 1)
                        }
                        variant="light"
                        disabled={item.quantity === 1}
                      >
                        <i className="fas fa-minus-circle"></i>
                      </Button>{' '}
                      <span>{item.quantity}</span>{' '}
                      <Button
                        onClick={() =>
                          updateCartHandler(item, item.quantity + 1)
                        }
                        variant="light"
                        disabled={item.quantity === item.countInStock}
                      >
                        <i className="fas fa-plus-circle"></i>
                      </Button>
                    </Col>
                    
                    <Col md={2}>
                      <Button
                        onClick={() => removeItemHandler(item)}
                        variant="light"
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <h3>
                    Subtotal (
                    {cartItems.reduce(
                      (prev, current) => prev + current.quantity,
                      0
                    )}{' '}
                    items) : 
                    {(cartItems.reduce((a, c) => a + c.price * c.quantity, 0) - discount).toFixed(2)} EGP
                  </h3>
                </ListGroup.Item>
                <ListGroup.Item>
                  <div className="d-grid">
                    <Button
                      onClick={checkOutHandler}
                      type="button"
                      variant="primary"
                      disabled={cartItems.length === 0}
                    >
                      Proceed to Checkout
                    </Button>
                  </div>
                </ListGroup.Item>
                <ListGroup.Item>
                  <form onSubmit={applyPromoHandler}>
                    <label htmlFor="promoCode">Promo Code</label>
                    <input
                      id="promoCode"
                      type="text"
                      value={promoCodeInput}
                      onChange={(e) => setPromoCode(e.target.value)}
                      className="form-control mb-2"
                    />
                    <Button
                      type="submit"
                      disabled={!promoCodeInput}
                    >
                      Apply
                    </Button>
                    {promoError && <div className="text-danger">{promoError}</div>}
                    {discount > 0 && (
                      <div className="text-success">
                        Discount applied: -{discount.toFixed(2)} EGP
                      </div>
                    )}
                  </form>
                </ListGroup.Item>
                <ListGroup.Item>
                  <h3>
                    Total:{" "}
                    {(
                      cartItems.reduce((a, c) => a + c.price * c.quantity, 0) -
                      discount
                    ).toFixed(2)}{" "}
                    EGP
                  </h3>
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
